/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { MouseEvent, memo } from "react";
import { Box, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, SxProps, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DEVICELIST, DEVICESTATUS, DIALOGOPENFOR } from "../../../globals/globals";
import { useStoreDispatch } from "../../../store/hooks";
import { setSelectedNavListItems } from "../../../store/helpers/helperReducers";
import { setSelectedDeviceSiteGroup, setSelectedDeviceStatus, setMaintenanceMode, setSelectedDeviceKpiStatus, setSelectedDeviceConfigStatus, setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { setSelectedGeoRegion } from "../../../store/api/admin/reducers";
import { useSelector } from "react-redux";
import { confirmDialogSelector, selectedNavListItemsSelector } from "../../../store/helpers/helperSelectors";
import { ISiteGroup } from "../../../store/api/devices/types";
import { constStr } from "../../../globals/constants";
import { setSelectedMaintenanceMode } from "../../../store/filters/events/EventFilterReducer";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { hasWhiteSpace } from "../../../utils/helpers";
import ConfirmDialog from "../../Shared/Dialogs/ConfirmDialog";
import { useNodeLeftSiteNav } from "../hooks";
import { initialGeoRegion } from "../../../components/helpers";

interface ISiteGroupListProps {
  permissions: ProfilePermissions | null;
  siteGroups?: ISiteGroup[] | [];
  headerTitle?: string;
  titleIcon?: any;
  showDelete?: boolean;
  onDelete?: (siteGroup: ISiteGroup) => void;
  //handlerListItem?: (arg1: string, arg2: string) => void;
}

const SiteGroupList: React.FC<ISiteGroupListProps> = ({ siteGroups, headerTitle, titleIcon, permissions, showDelete, onDelete }) => {
  const dispatch = useStoreDispatch();
  const { ALLDEVICES, DISCONNECTED } = DEVICELIST;
  const { deleteHandler, handleDeleteAction, setSelectedSiteGroup, selectedSiteGroup } = useNodeLeftSiteNav();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const { nodeItemIndex } = useSelector(selectedNavListItemsSelector);

  const handleListItemClick = (siteGroup: ISiteGroup) => {
    setSelectedSiteGroup?.(siteGroup);
    //handling nav list
    if (siteGroup.name === ALLDEVICES.Text) {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus(""));

      //activates Events filter
      dispatch(setSelectedMaintenanceMode(""));
    } else if (siteGroup.name === DISCONNECTED.Text) {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(DEVICESTATUS.TIMEOUT)); // "TIMEOUT" is for disconnect device
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus(""));

      //activates Events filter
      dispatch(setSelectedMaintenanceMode(""));
    } else if (siteGroup.objectKey === constStr.initialRegionObjectKey) {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setSelectedDeviceSiteGroup(constStr.ungrouped));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus(""));

      //activates Events filter
      dispatch(setSelectedMaintenanceMode(""));
    } else if (siteGroup.objectKey === "maintenancemode") {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setMaintenanceMode("true"));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus(""));

      //activates Events filter
      dispatch(setSelectedMaintenanceMode("1"));
    } else if (siteGroup.objectKey === "pmCollected") {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setSelectedMaintenanceMode(""));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus("false"));
      dispatch(setSelectedDeviceConfigStatus(""));
    } else if (siteGroup.objectKey === "offline") {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setSelectedMaintenanceMode(""));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus("Offline"));
    } else if (siteGroup.objectKey === "configMismatch") {
      dispatch(setSelectedGeoRegion(initialGeoRegion));
      dispatch(setRegionFilterIds([]));
      dispatch(setSelectedDeviceStatus(""));
      dispatch(setSelectedMaintenanceMode(""));
      dispatch(setSelectedDeviceSiteGroup(""));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus("Mismatch"));
    } else {
      dispatch(setSelectedDeviceSiteGroup(siteGroup.objectKey!));
      dispatch(setMaintenanceMode(""));
      dispatch(setSelectedDeviceStatus(""));

      //activates Events filter
      dispatch(setSelectedMaintenanceMode(""));
      dispatch(setSelectedDeviceKpiStatus(""));
      dispatch(setSelectedDeviceConfigStatus(""));

      // Sync region filter to the device group's parent region
      if (siteGroup.parentKey) {
        dispatch(setRegionFilterIds([siteGroup.parentKey]));
      } else {
        dispatch(setRegionFilterIds([]));
      }
    }
    dispatch(setSelectedNavListItems(siteGroup.objectKey!));
  };

  const ListItemTextSxProps: SxProps = {
    textDecoration: "none",
    fontWeight: "normal",
    fontSize: 'var(--font-size-sm)',
  };

  const ListItemIconSxProps: SxProps = {
    minWidth: '26px',
  };
  const listItemButtonSxprops: SxProps = {
    pl: 0.5,
    py: 0.25,
    minHeight: 32,
    '&:hover': { backgroundColor: 'var(--sidebar-accent)' },
    "&.Mui-selected": {
      backgroundColor: "var(--sidebar-accent) !important",
      borderRadius: 'calc(var(--radius) - 2px)',
    },
  };
  const listTextSxProps: SxProps = {
    fontWeight: 400,
    fontSize: 'var(--font-size-sm)',
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: 'inherit',
  };
  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.SITEGROUPS && !onDelete && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteAction}>
          Are you sure you want to delete <strong>{selectedSiteGroup?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <div style={{ width: "100%" }}>
        {headerTitle && (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mr: 0.5 }}>
            <Typography component="span" sx={{ display: 'flex', height: 32, alignItems: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'inherit', opacity: 0.7 }}>
              {headerTitle}
            </Typography>
            {titleIcon && titleIcon}
          </Box>
        )}
     <List disablePadding sx={{
          width: "100%",
          ...((siteGroups?.length ?? 0) > 5 && {
            maxHeight: "260px",
            overflow: "auto",
            "&::-webkit-scrollbar": { height: "10px", width: "6px", background: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "var(--border)", borderRadius: "5px" },
            "&::-webkit-scrollbar-corner": { background: "transparent" },
          }),
        }}>
          {siteGroups?.map((siteGroup: ISiteGroup) => {
            return (
              <ListItem
                disablePadding
                key={siteGroup.name}
                onClick={(e: MouseEvent) => handleListItemClick(siteGroup)}
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{siteGroup.deviceCount || 0}</span>
                    {permissions?.hasOpCodeGroup("manage_topology") && showDelete && (
                      <IconButton
                        disabled={(siteGroup && siteGroup.deviceCount && siteGroup.deviceCount > 0) || siteGroup.objectKey.length === 0 || false}
                        aria-label="Delete site group"
                        component="label"
                        size="small"
                        edge="end"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (onDelete) {
                            onDelete(siteGroup);
                          } else {
                            setSelectedSiteGroup?.(siteGroup);
                            deleteHandler?.(siteGroup);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" sx={{ color: 'inherit' }} />
                      </IconButton>
                    )}
                  </Box>
                }
              >
                <ListItemButton sx={listItemButtonSxprops} selected={nodeItemIndex === siteGroup?.objectKey}>
                  {siteGroup.icon && (
                    <ListItemIcon sx={ListItemIconSxProps}>
                      <siteGroup.icon sx={{ fontSize: 18, color: 'inherit' }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    title={siteGroup.name}
                    primary={siteGroup.name}
                    sx={ListItemTextSxProps}
                    primaryTypographyProps={{ sx: !hasWhiteSpace(siteGroup?.name) ? listTextSxProps : { fontWeight: 400, fontSize: 'var(--font-size-sm)', color: 'inherit' } }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </div>
    </>
  );
};

export default memo(SiteGroupList);
